import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import Welcome from "@/pages/Welcome";
import AuthPage from "@/pages/Auth";
import Dashboard from "@/pages/Dashboard";
import Program from "@/pages/Program";
import WeeklyProgram from "@/pages/WeeklyProgram";
import DailySession from "@/pages/DailySession";
import CurrentExercises from "@/pages/CurrentExercises";
import Education from "@/pages/Education";
import ProgressPage from "@/pages/Progress";
import FlareUp from "@/pages/FlareUp";
import Badges from "@/pages/Badges";
import Admin from "@/pages/Admin";
import ExerciseLibrary from "@/pages/ExerciseLibrary";
import ExerciseProgramSharing from "@/pages/ExerciseProgramSharing";
import PatientExerciseProgram from "@/pages/PatientExerciseProgram";
import BulkExerciseImport from "@/pages/BulkExerciseImport";
import AdminPlaylists from "@/pages/AdminPlaylists";
import AdminPatientPlans from "@/pages/AdminPatientPlans";
import AdminFeedback from "@/pages/AdminFeedback";
import AdminTherapists from "@/pages/AdminTherapists";
import PatientDirectory from "@/pages/PatientDirectory";
import PatientProgressReview from "@/pages/PatientProgressReview";
import Profile from "@/pages/Profile";
import MobileShell from "@/components/MobileShell";
import DemoNotice from "@/components/DemoNotice";

function Protected({ children, roles }) {
  const { user } = useAuth();
  if (user === undefined)
    return (
      <div className="min-h-screen flex items-center justify-center text-slate-500" data-testid="auth-loading">
        Loading…
      </div>
    );
  if (user === null) return <Navigate to="/auth" replace />;
  if (roles && !roles.includes(user.role)) {
    return (
      <MobileShell>
        <div className="px-6 pt-8">
          <p className="text-base font-semibold text-slate-900">Access not available</p>
          <p className="mt-2 text-sm text-slate-600">Your account does not have permission to view this page.</p>
          <div className="mt-6">
            <Button onClick={() => window.location.assign(user.role === "patient" ? "/dashboard" : user.role === "therapist" ? "/my-patients" : "/admin")}>Return to dashboard</Button>
          </div>
        </div>
      </MobileShell>
    );
  }
  return children;
}

function StaffLanding() {
  const { user } = useAuth();
  return user?.role === "therapist" ? <Navigate to="/my-patients" replace /> : <Admin />;
}

function AppRoutes() {
  const patientRoles = ["patient"];
  const clinicalRoles = ["admin", "therapist"];
  const adminRoles = ["admin"];
  const therapistRoles = ["therapist"];

  return (
    <Routes>
      <Route path="/" element={<Welcome />} />
      <Route path="/auth" element={<AuthPage />} />
      <Route path="/conditions" element={<Protected roles={patientRoles}><Navigate to="/dashboard" replace /></Protected>} />
      <Route path="/intake" element={<Protected roles={patientRoles}><Navigate to="/dashboard" replace /></Protected>} />
      <Route path="/dashboard" element={<Protected roles={patientRoles}><Dashboard /></Protected>} />
      <Route path="/program" element={<Protected roles={patientRoles}><Program /></Protected>} />
      <Route path="/weekly" element={<Protected roles={patientRoles}><WeeklyProgram /></Protected>} />
      <Route path="/session/:weekIdx/:dayIdx" element={<Protected roles={patientRoles}><DailySession /></Protected>} />
      <Route path="/exercises" element={<Protected roles={patientRoles}><CurrentExercises /></Protected>} />
      <Route path="/education" element={<Protected roles={patientRoles}><Education /></Protected>} />
      <Route path="/progress" element={<Protected roles={patientRoles}><ProgressPage /></Protected>} />
      <Route path="/flare-up" element={<Protected roles={patientRoles}><FlareUp /></Protected>} />
      <Route path="/badges" element={<Protected roles={patientRoles}><Badges /></Protected>} />
      <Route path="/profile" element={<Protected roles={patientRoles}><Profile /></Protected>} />
      <Route path="/admin" element={<Protected roles={clinicalRoles}><StaffLanding /></Protected>} />
      <Route path="/admin/feedback" element={<Protected roles={adminRoles}><AdminFeedback /></Protected>} />
      <Route path="/admin/therapists" element={<Protected roles={adminRoles}><AdminTherapists /></Protected>} />
      <Route path="/admin/patients" element={<Protected roles={adminRoles}><PatientDirectory adminMode /></Protected>} />
      <Route path="/my-patients" element={<Protected roles={therapistRoles}><PatientDirectory /></Protected>} />
      <Route path="/admin/exercises" element={<Protected roles={clinicalRoles}><ExerciseLibrary /></Protected>} />
      <Route path="/admin/exercise-programs/new" element={<Protected roles={clinicalRoles}><ExerciseProgramSharing /></Protected>} />
      <Route path="/admin/exercise-programs/:programId" element={<Protected roles={clinicalRoles}><ExerciseProgramSharing /></Protected>} />
      <Route path="/patient/exercise-program" element={<PatientExerciseProgram />} />
      <Route path="/admin/exercises/bulk-import" element={<Protected roles={adminRoles}><BulkExerciseImport /></Protected>} />
      <Route path="/admin/playlists" element={<Protected roles={clinicalRoles}><AdminPlaylists /></Protected>} />
      <Route path="/admin/patients/:patientId/plans" element={<Protected roles={clinicalRoles}><AdminPatientPlans /></Protected>} />
      <Route path="/admin/patients/:patientId/progress" element={<Protected roles={clinicalRoles}><PatientProgressReview /></Protected>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <DemoNotice />
        <AppRoutes />
        <Toaster position="top-center" />
      </BrowserRouter>
    </AuthProvider>
  );
}
